import { useEffect, useMemo, useRef, useState } from "react";
import { getSendFromOptions, type SendFromOption, type SendFromOptions } from "@/services/communications.service";
import { formatUsPhone } from "@/lib/phone";
import { groupByFacility } from "./groupByFacility";

/** The last ten digits, the form the server lists numbers in. */
function tenDigits(value: string | null | undefined): string {
  return (value ?? "").replace(/\D/g, "").slice(-10);
}

/**
 * The composer's "Send from" choice: which of the practice's numbers a text
 * goes out from.
 *
 * Where the choice starts, and when it moves:
 *   - a patient's thread opens: the number a send with no choice would use;
 *   - staff pick a message to reply to: the line that message used, when it
 *     is still one of the practice's numbers, else that same default;
 *   - staff pick a number.
 * Nothing else moves it. A send, or the reply strip clearing after one, leaves
 * it where it is, so a conversation stays on one line. Replying to the same
 * message again moves it back to that message's line.
 *
 * `fromNumber` is what to send: set only while the picker is shown (two or
 * more numbers, list loaded) and the choice is not the default. Otherwise
 * undefined, and the server picks the number as it always has, which is the
 * default, or a working line if the default was removed since the list loaded.
 *
 * `needsChoice` is true while the picker is shown with nothing chosen (the
 * patient's default is not one of the numbers); a send then has no sender.
 */
export function useSendFromChoice(
  patientId: string | undefined,
  reply: { id: string; practiceNumber?: string } | null | undefined,
) {
  const [data, setData] = useState<SendFromOptions | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const appliedReplyId = useRef<string | null>(null);

  useEffect(() => {
    setData(null);
    setSelected(null);
    appliedReplyId.current = null;
    if (!patientId || !Number(patientId)) return;

    let cancelled = false;
    // Inside the chain, so a loader that throws before returning a promise
    // hides the picker like any other failure instead of breaking the thread.
    Promise.resolve()
      .then(() => getSendFromOptions(Number(patientId)))
      .then((loaded) => {
        if (cancelled) return;
        const listed = new Set(loaded.options.map((o) => o.number));
        setData(loaded);
        setSelected(loaded.defaultNumber && listed.has(loaded.defaultNumber) ? loaded.defaultNumber : null);
      })
      .catch(() => {
        // No list: the picker stays hidden and texts send from the usual number.
      });
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  const replyId = reply?.id ?? null;
  const replyLine = tenDigits(reply?.practiceNumber);
  useEffect(() => {
    // The strip closed: forget the reply, so choosing it again re-applies its
    // line. The choice itself stays where it is.
    if (!replyId) {
      appliedReplyId.current = null;
      return;
    }
    if (!data || appliedReplyId.current === replyId) return;
    appliedReplyId.current = replyId;
    const listed = data.options.some((o) => o.number === replyLine);
    const fallback = data.defaultNumber && data.options.some((o) => o.number === data.defaultNumber)
      ? data.defaultNumber
      : null;
    setSelected(listed ? replyLine : fallback);
  }, [data, replyId, replyLine]);

  const options = data?.options ?? [];
  const visible = options.length > 1;
  const selectedOption = options.find((o) => o.number === selected) ?? null;

  return {
    options,
    visible,
    selected,
    setSelected,
    selectedOption,
    fromNumber: visible && selected && selected !== data?.defaultNumber ? selected : undefined,
    needsChoice: visible && selected === null,
  };
}

export function SendFromPicker({
  options,
  value,
  onChange,
  hint,
  disabled,
}: {
  options: SendFromOption[];
  value: string | null;
  onChange: (number: string) => void;
  hint: string | null;
  disabled?: boolean;
}) {
  const groups = useMemo(() => groupByFacility(options), [options]);

  return (
    <div className="mb-2">
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground flex-shrink-0">Send from</span>
        <select
          className="h-8 min-w-0 flex-1 md:flex-none md:w-auto md:max-w-full text-xs rounded-md border border-border bg-white px-2"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          aria-label="Send from"
        >
          {value === null && <option value="">Choose a number…</option>}
          {groups.map((group, index) => (
            <optgroup key={`${index}-${group.name}`} label={group.name}>
              {group.options.map((o) => (
                <option key={o.number} value={o.number}>
                  {formatUsPhone(o.number) || o.number} · {o.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>
      {hint && <p className="mt-1 text-xs text-muted-foreground leading-snug">{hint}</p>}
    </div>
  );
}
